// OptionsCard.tsx
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowUpDown, Clock, Accessibility, Bike, Train, TrainFront, Bus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { purpleColors, violetColors, slateColors, fontFamily } from '../ContentPages/AppLandingPage/styles';
import * as Buttons from '../Controls/Buttons';
import * as Dropdowns from '../Controls/Dropdowns';
import Switch from '../Controls/Switches';


const FrameContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 90%;
  max-width: 500px;
  max-height: 90%;
  overflow-y: auto;
  position: relative;
  box-sizing: border-box;
  border: 1px solid ${slateColors[100]};
  
  @media (min-width: 640px) {
    padding: 2.5rem;
  }
  
  @media (min-width: 768px) {
    width: 90%;
    max-width: 500px;
    padding: 3rem;
    border-radius: 2rem;
  }
  
  @media (min-width: 1024px) {
    max-width: 500px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  align-self: stretch;
`;

const OptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  align-self: stretch;
`;

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
`;

const SectionTitle = styled.h3`
  font-family: ${fontFamily};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${slateColors[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
  
  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  align-self: stretch;
  padding: 0.25rem 0;
`;

const SuburbanIcon = styled.div`
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${purpleColors[600]};
  border-radius: 50%;
  color: ${purpleColors[600]};
  font-family: ${fontFamily};
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  box-sizing: border-box;
`;

const SettingLabel = styled.label`
  font-family: ${fontFamily};
  flex: 1 0 0;  
  font-size: 1rem;
  font-weight: 500;
  color: ${slateColors[800]};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
  }
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

interface OptionsCardProps {
  onClose: () => void;
  onApply: (options: Record<string, any>) => void;
  initialOptions: Record<string, any>;
}

const defaultValues = {
  transfers: -1,
  transferTime: 0,
  accessibility: 'none',
  bike: false,
  longdistance: true,
  regional: true,
  suburban: true,
  local: true,
};

const OptionsCard = ({ onClose, onApply, initialOptions }: OptionsCardProps) => {
  const { t } = useTranslation();
  // Convert to string to match dropdown values which are strings
  const [numberOfTransfers, setNumberOfTransfers] = useState<string>(
    initialOptions.transfers !== undefined ? String(initialOptions.transfers) : String(defaultValues.transfers)
  );
  const [minimumTransferTime, setMinimumTransferTime] = useState<string>(
    initialOptions.transferTime !== undefined ? String(initialOptions.transferTime) : String(defaultValues.transferTime)
  );
  const [accessibleJourneys, setAccessibleJourneys] = useState(initialOptions.accessibility || defaultValues.accessibility);
  const [bikeFriendly, setBikeFriendly] = useState(initialOptions.bike || defaultValues.bike);
  
  const [longDistanceTrains, setLongDistanceTrains] = useState<boolean>(initialOptions.longdistance);
  const [regionalTrains, setRegionalTrains] = useState<boolean>(initialOptions.regional);
  const [suburbanTrains, setSuburbanTrains] = useState<boolean>(initialOptions.suburban);
  const [localPublicTransport, setLocalPublicTransport] = useState<boolean>(initialOptions.local);

  // Sync state when initialOptions changes (e.g., when loading from URL)
  useEffect(() => {
    // Convert to string to match dropdown values
    const transfersValue = initialOptions.transfers !== undefined 
      ? String(initialOptions.transfers)
      : String(defaultValues.transfers);
    setNumberOfTransfers(transfersValue);
    
    // Convert to string to match dropdown values
    const transferTimeValue = initialOptions.transferTime !== undefined
      ? String(initialOptions.transferTime)
      : String(defaultValues.transferTime);
    setMinimumTransferTime(transferTimeValue);
    
    setAccessibleJourneys(initialOptions.accessibility !== undefined ? initialOptions.accessibility : defaultValues.accessibility);
    setBikeFriendly(initialOptions.bike !== undefined ? initialOptions.bike : defaultValues.bike);
    setLongDistanceTrains(initialOptions.longdistance !== undefined ? initialOptions.longdistance : defaultValues.longdistance);
    setRegionalTrains(initialOptions.regional !== undefined ? initialOptions.regional : defaultValues.regional);
    setSuburbanTrains(initialOptions.suburban !== undefined ? initialOptions.suburban : defaultValues.suburban);
    setLocalPublicTransport(initialOptions.local !== undefined ? initialOptions.local : defaultValues.local);
  }, [initialOptions]);

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Compare current state with initialOptions to check for changes
  useEffect(() => {
    // Create an object with the current state values
    // Convert string values to numbers for comparison with initialOptions
    const currentOptions = {
      transfers: parseInt(numberOfTransfers, 10),
      transferTime: parseInt(minimumTransferTime, 10),
      accessibility: accessibleJourneys,
      bike: bikeFriendly,
      longdistance: longDistanceTrains,
      regional: regionalTrains,
      suburban: suburbanTrains,
      local: localPublicTransport
    };
  
    // Compare the current options with the initial options
    const isChanged = JSON.stringify(currentOptions) !== JSON.stringify(initialOptions);
  
    // Update hasChanges based on whether there are changes
    setHasChanges(isChanged);
  }, [
    numberOfTransfers,
    minimumTransferTime,
    accessibleJourneys,
    bikeFriendly,
    longDistanceTrains,
    regionalTrains,
    suburbanTrains,
    localPublicTransport,
    initialOptions
  ]);

  // Function to reset all state variables to their initial values
  const handleReset = () => {
    setNumberOfTransfers(String(defaultValues.transfers));
    setMinimumTransferTime(String(defaultValues.transferTime));
    setAccessibleJourneys(defaultValues.accessibility);
    setBikeFriendly(defaultValues.bike);
    setLongDistanceTrains(defaultValues.longdistance);
    setRegionalTrains(defaultValues.regional);
    setSuburbanTrains(defaultValues.suburban);
    setLocalPublicTransport(defaultValues.local);
  };

  const handleApply = () => {
    const options = {
      transfers: parseInt(numberOfTransfers, 10), // Convert string back to number for API
      transferTime: parseInt(minimumTransferTime, 10), // Convert string back to number for API
      accessibility: accessibleJourneys,
      bike: bikeFriendly,
      longdistance: longDistanceTrains,
      regional: regionalTrains,
      suburban: suburbanTrains,
      local: localPublicTransport,
    };
    onApply(options);
    setHasChanges(false)
    onClose();
  };

  return (
    <FrameContainer>
      <HeaderSection>
        <Buttons.CloseButton onClick={onClose} disabled={false} />
        <Buttons.TextButtonM onClick={handleReset}>{t('searchControls.optionsCard.reset')}</Buttons.TextButtonM>
      </HeaderSection>
        <OptionsList>
          <OptionsSection>
            <SectionTitle>{t('searchControls.optionsCard.journeyOptions')}</SectionTitle>
            <SettingsList>
              <SettingItem>
                <SettingLabel htmlFor="number-of-transfers">
                  <ArrowUpDown />
                  {t('searchControls.optionsCard.numberOfTransfers')}
                </SettingLabel>
                <Dropdowns.DropdownS
                  id="number-of-transfers"
                  value={numberOfTransfers}
                  onChange={(e) => setNumberOfTransfers(e.target.value)}
                >
                  <option value="-1">{t('searchControls.optionsCard.transfersAny')}</option>
                  <option value="1">{t('searchControls.optionsCard.transfersMax1')}</option>
                  <option value="2">{t('searchControls.optionsCard.transfersMax2')}</option>
                  <option value="3">{t('searchControls.optionsCard.transfersMax3')}</option>
                  <option value="0">{t('searchControls.optionsCard.transfersDirect')}</option>
                </Dropdowns.DropdownS>
              </SettingItem>

              <SettingItem>
                <SettingLabel htmlFor="minimum-transfer-time">
                  <Clock />
                  {t('searchControls.optionsCard.minimumTransferTime')}
                </SettingLabel>
                <Dropdowns.DropdownS
                  id="minimum-transfer-time"
                  value={minimumTransferTime}
                  onChange={(e) => setMinimumTransferTime(e.target.value)}
                >
                  <option value="0">{t('searchControls.optionsCard.transferTimeNone')}</option>
                  <option value="5">{t('searchControls.optionsCard.transferTime5min')}</option>
                  <option value="15">{t('searchControls.optionsCard.transferTime15min')}</option>
                  <option value="30">{t('searchControls.optionsCard.transferTime30min')}</option>
                  <option value="60">{t('searchControls.optionsCard.transferTime1hour')}</option>
                </Dropdowns.DropdownS>
              </SettingItem>

              <SettingItem>
                <SettingLabel htmlFor="accessible-journeys">
                  <Accessibility />
                  {t('searchControls.optionsCard.accessibleJourneys')}
                </SettingLabel>
                <Dropdowns.DropdownS
                  id="accessible-journeys"
                  value={accessibleJourneys}
                  onChange={(e) => setAccessibleJourneys(e.target.value)}
                >
                  <option value="none">{t('searchControls.optionsCard.accessibilityNo')}</option>
                  <option value="partial">{t('searchControls.optionsCard.accessibilityPartial')}</option>
                  <option value="complete">{t('searchControls.optionsCard.accessibilityYes')}</option>
                </Dropdowns.DropdownS>
              </SettingItem>

              <SettingItem>
                <SettingLabel htmlFor="bike-friendly">
                  <Bike />
                  {t('searchControls.optionsCard.bikeFriendly')}
                </SettingLabel>
                <Switch
                  id="bike-friendly"
                  toggled={bikeFriendly}
                  onChange={(e) => setBikeFriendly(e.target.checked)}
                />
              </SettingItem>
            </SettingsList>
          </OptionsSection>

          <OptionsSection>
            <SectionTitle>{t('searchControls.optionsCard.transportModes')}</SectionTitle>
            <SettingsList>
              <SettingItem>
                <SettingLabel htmlFor="long-distance">
                  <TrainFront />
                  {t('searchControls.optionsCard.longDistance')}
                </SettingLabel>
                <Switch
                  id="long-distance"
                  toggled={longDistanceTrains}
                  onChange={(e) => setLongDistanceTrains(e.target.checked)}
                />
              </SettingItem>

              <SettingItem>
                <SettingLabel htmlFor="regional">
                  <Train />
                  {t('searchControls.optionsCard.regional')}
                </SettingLabel>
                <Switch
                  id="regional"
                  toggled={regionalTrains}
                  onChange={(e) => setRegionalTrains(e.target.checked)}
                />
              </SettingItem>

              <SettingItem>
                <SettingLabel htmlFor="suburban">
                  <SuburbanIcon>S</SuburbanIcon>
                  {t('searchControls.optionsCard.suburban')}
                </SettingLabel>
                <Switch
                  id="suburban"
                  toggled={suburbanTrains}
                  onChange={(e) => setSuburbanTrains(e.target.checked)}
                />
              </SettingItem>

              <SettingItem>
                <SettingLabel htmlFor="local">
                  <Bus />
                  {t('searchControls.optionsCard.local')}
                </SettingLabel>
                <Switch
                  id="local"
                  toggled={localPublicTransport}
                  onChange={(e) => setLocalPublicTransport(e.target.checked)}
                />
              </SettingItem>
            </SettingsList>
          </OptionsSection>
        </OptionsList>
      <Buttons.CTAButtonFilled onClick={handleApply} disabled={!hasChanges}>
        {t('searchControls.optionsCard.apply')}
      </Buttons.CTAButtonFilled>
    </FrameContainer>
  );
};

export default OptionsCard;
